/**
 * Overview tab body. (File still named HomeTab.tsx from the prior
 * iteration; flag for a rename pass when convenient.)
 *
 * Single source of "where is this property at" + answers to the
 * questions a property manager will ask the operator most often:
 *
 *   Q1 Where is my Charter?              → Section "Charter"
 *   Q2 How do I log in to Charter Suite? → Section "Charter" (buttons)
 *   Q3 Where is my bedside certificate?  → Section "Bedside certificate"
 *
 * Five compact sections, each in a premium-card with a clear heading.
 * Mobile-friendly: cards stack vertically, button rows flex-wrap,
 * label-value pairs use data-grid which collapses cleanly.
 *
 *   1. Status              — assurance + temperature, last service,
 *                            contract, plus the editable assurance
 *                            dates (Certified Since, Assured Until).
 *   2. Charter             — number, issued, delivered + courier
 *                            notes (editable), Open Suite + Send
 *                            Suite Invite buttons.
 *   3. Bedside certificate — POS Certificate generator + QR download.
 *   4. People              — customer + contact, ambassador, manager
 *                            (all editable in edit mode).
 *   5. Public pages        — Verify page URL + Public directory listing
 *                            URL, each with Open + Copy.
 *
 * Replaces the prior Home + Accreditation tabs. CertificationTab is
 * dissolved; its content lives in sections 1, 2, 3 + 5 here.
 */

import { useEffect, useState } from 'react';
import { formatDateMedium } from '../../../../../utils/formatters';
import { useNavigate } from 'react-router-dom';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useAuth } from '../../../../../contexts/AuthContext';
import CustomerForm from '../../../../../components/CustomerForm';
import SearchSelect from '../../../../../components/SearchSelect';
import EmailWithStatus from '../../../../../components/EmailWithStatus';
import { POS_GOLD } from '../constants';
import { downloadQrAsPng } from './QrDownloadPanel';
import type { EnrichedProperty, PropertyEditForm, PropertyMode } from './types';

interface OverviewTabProps {
  supabase: SupabaseClient;
  property: EnrichedProperty;
  form: PropertyEditForm;
  setForm: (updater: (f: PropertyEditForm) => PropertyEditForm) => void;
  mode: PropertyMode;
}

function formatDate(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return formatDateMedium(d);
}

const TEMP_COLORS: Record<string, { bg: string; fg: string; label: string }> = {
  green: { bg: 'var(--color-success-bg)', fg: 'var(--color-success)', label: 'Green' },
  yellow: { bg: '#fef9c3', fg: '#a16207', label: 'Yellow' },
  orange: { bg: '#ffedd5', fg: 'var(--color-warning-text)', label: 'Orange' },
  red: { bg: 'var(--color-error-bg)', fg: 'var(--color-error)', label: 'Red' },
};

function deriveTemperature(assuredUntil: string | null): 'red' | 'orange' | 'yellow' | 'green' | null {
  if (!assuredUntil) return null;
  const days = Math.floor((new Date(assuredUntil).getTime() - Date.now()) / 86400000);
  if (days < 0) return 'red';
  if (days <= 30) return 'orange';
  if (days <= 90) return 'yellow';
  return 'green';
}

/**
 * Region code lookup for the public listing URL. Mirrors the public
 * site's apps/peace-of-sleep/src/lib/regions.ts. Province display name
 * (e.g. "Western Cape") → short region code ("wc"). Returns null if
 * we can't resolve, in which case the listing URL block stays hidden.
 */
const REGION_CODES: Record<string, Record<string, string>> = {
  za: {
    'western cape': 'wc',
    'gauteng': 'gt',
    'eastern cape': 'ec',
    'kwazulu-natal': 'kzn',
    'limpopo': 'lp',
    'north west': 'nw',
    'mpumalanga': 'mp',
    'free state': 'fs',
    'northern cape': 'nc',
  },
  dk: {
    'sjælland': 'sj',
    'jylland': 'jy',
    'fyn': 'fy',
  },
};
function getRegionCode(country: string | null | undefined, province: string | null | undefined): string | null {
  if (!country || !province) return null;
  const map = REGION_CODES[country.toLowerCase()];
  if (!map) return null;
  return map[province.toLowerCase().trim()] ?? null;
}

function CopyButton({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-sm"
      onClick={() => {
        navigator.clipboard.writeText(value).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? '✓ Copied' : (label ?? 'Copy')}
    </button>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="premium-card">
      <div className="premium-card-header">
        <h3>{title}</h3>
      </div>
      <div className="premium-card-body">
        {children}
      </div>
    </div>
  );
}

/**
 * Label + value field cell, used inside the grid layouts on this tab.
 * Renders the small uppercase label above the value. The value is
 * whatever the caller passes — a string, an input, a dropdown, etc.
 */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{
        fontSize: 'var(--text-xs)',
        fontWeight: 600,
        color: 'var(--color-text-light)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        marginBottom: '0.25rem',
      }}>{label}</div>
      <div style={{ fontSize: 'var(--text-base)' }}>{children}</div>
    </div>
  );
}

const TWO_COL_GRID: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
  gap: '1rem 1.25rem',
};

export default function OverviewTab({ supabase, property, form, setForm, mode }: OverviewTabProps) {
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = useAuth();
  // Ambassador attribution is a payout decision, locked to payouts:manage admins
  // server-side (hosp_properties_guard_manager_columns). Mirror it here so a
  // non-admin sees the ambassador read-only rather than a control that silently
  // reverts on save.
  const canSetAmbassador = isSuperAdmin || hasPermission('payouts', 'manage');
  const tempKey = deriveTemperature(property.assured_until);
  const temp = tempKey ? TEMP_COLORS[tempKey] : null;
  const readOnly = mode === 'view';
  const cc = (property.country || 'za').toLowerCase();
  const verifyUrl = property.charter_number
    ? `https://peaceofsleep.vip/${cc}/verify/${encodeURIComponent(property.charter_number)}/`
    : null;
  const regionCode = getRegionCode(property.country, property.province);
  const listingUrl = property.is_published && regionCode && property.slug
    ? `https://peaceofsleep.vip/${cc}/${regionCode}/${property.slug}/`
    : null;
  const posCertUrl = property.charter_number
    ? `/pos-certificate-v2.html?charter=${encodeURIComponent(property.charter_number)}`
    : null;

  const [ambassadors, setAmbassadors] = useState<{ id: string; name: string }[]>([]);
  const [assignableUsers, setAssignableUsers] = useState<{ id: string; name: string }[]>([]);
  const [showCustomerPicker, setShowCustomerPicker] = useState(false);

  useEffect(() => {
    if (mode !== 'edit') return;
    supabase
      .from('users')
      .select('id, first_name, last_name')
      .eq('is_ambassador', true)
      .eq('status', 'active')
      .order('first_name')
      .then(({ data }) =>
        setAmbassadors((data || []).map((u: Record<string, unknown>) => ({
          id: u.id as string,
          name: `${(u.first_name as string) || ''} ${(u.last_name as string) || ''}`.trim(),
        }))),
      );
    supabase
      .from('users')
      .select('id, first_name, last_name')
      .eq('status', 'active')
      .order('first_name')
      .then(({ data }) =>
        setAssignableUsers((data || []).map((u: Record<string, unknown>) => ({
          id: u.id as string,
          name: `${(u.first_name as string) || ''} ${(u.last_name as string) || ''}`.trim(),
        }))),
      );
  }, [supabase, mode]);

  function onCustomerPicked(customer: Record<string, unknown>) {
    const c = customer as Record<string, string | null>;
    setForm((f) => ({ ...f, customer_id: (c.id as string) || '' }));
    setShowCustomerPicker(false);
  }

  const customerName = property.customer?.company_name
    || property.customer?.customer_name
    || 'No customer linked';
  const ambassadorDisplay = property.ambassador
    ? `${property.ambassador.first_name || ''} ${property.ambassador.last_name || ''}`.trim() || property.ambassador.email || 'None'
    : 'None assigned';
  const managerDisplay = property.manager
    ? `${property.manager.first_name || ''} ${property.manager.last_name || ''}`.trim() || property.manager.email || 'None'
    : 'None assigned';
  const customerChangeStaged = mode === 'edit' && form.customer_id && form.customer_id !== property.customer_id;

  async function openCharterSuite() {
    if (!property.charter_number) return;
    const charter = property.charter_number;
    try {
      const { data, error } = await supabase.functions.invoke('pos-charter-suite-admin-handoff', {
        body: { charter, country: cc },
      });
      if (error || !data?.ok || !data?.url) {
        window.open(`https://peaceofsleep.vip/${cc}/charter-suite/${encodeURIComponent(charter)}/`, '_blank', 'noopener,noreferrer');
        return;
      }
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch {
      window.open(`https://peaceofsleep.vip/${cc}/charter-suite/${encodeURIComponent(charter)}/`, '_blank', 'noopener,noreferrer');
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* 1. STATUS */}
      <SectionCard title="Status">
        {/* Row 1: Assurance + temperature pill — full width to host the pill cleanly. */}
        <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--color-border, #e5e7eb)' }}>
          <Field label="Assurance">
            <span style={{ fontWeight: 500 }}>
              {property.assured_until
                ? `${tempKey === 'red' ? 'Expired on' : 'Assured until'} ${formatDate(property.assured_until)}`
                : 'No assurance period on file'}
            </span>
            {temp && (
              <span style={{
                marginLeft: '0.5rem', padding: '0.125rem 0.5rem', borderRadius: '999px',
                fontSize: 'var(--text-xs)', fontWeight: 600, background: temp.bg, color: temp.fg,
                textTransform: 'uppercase', letterSpacing: '0.03em', verticalAlign: 'middle',
              }}>{temp.label}</span>
            )}
          </Field>
        </div>
        {/* Row 2: Last service + Contract */}
        <div style={{ ...TWO_COL_GRID, marginBottom: '1rem' }}>
          <Field label="Last service">{formatDate(property.last_service_date)}</Field>
          <Field label="Contract"><span className="text-capitalize">{property.contract_status || '—'}</span></Field>
        </div>
        {/* Row 3: Certified since + Assured until (editable in edit mode) */}
        <div style={TWO_COL_GRID}>
          <Field label="Certified since">
            {readOnly ? formatDate(property.certified_since) : (
              <input
                type="date"
                className="form-input"
                value={form.certified_since}
                onChange={(e) => setForm((f) => ({ ...f, certified_since: e.target.value }))}
                style={{ maxWidth: '12rem', fontSize: 'var(--text-base)' }}
              />
            )}
          </Field>
          <Field label="Assured until">
            {readOnly ? formatDate(property.assured_until) : (
              <input
                type="date"
                className="form-input"
                value={form.assured_until}
                onChange={(e) => setForm((f) => ({ ...f, assured_until: e.target.value }))}
                style={{ maxWidth: '12rem', fontSize: 'var(--text-base)' }}
              />
            )}
          </Field>
        </div>
      </SectionCard>

      {/* 2. CHARTER */}
      <SectionCard title="Charter">
        {property.charter_number ? (
          <>
            <div className="data-grid">
              <div className="data-grid-item">
                <span className="data-grid-label">Number</span>
                <span className="data-grid-value">
                  <span className="ref-code">{property.charter_number}</span>
                </span>
              </div>
              <div className="data-grid-item">
                <span className="data-grid-label">Issued</span>
                <span className="data-grid-value">{formatDate(property.charter_issued_at)}</span>
              </div>
              <div className="data-grid-item">
                <span className="data-grid-label">To</span>
                <span className="data-grid-value">
                  {readOnly ? (
                    property.charter_delivery_to || '—'
                  ) : (
                    <input
                      type="text"
                      className="form-input"
                      value={form.charter_delivery_to}
                      onChange={(e) => setForm((f) => ({ ...f, charter_delivery_to: e.target.value }))}
                      placeholder="Recipient the framed Charter is sent to"
                      style={{ fontSize: 'var(--text-base)' }}
                    />
                  )}
                </span>
              </div>
              <div className="data-grid-item">
                <span className="data-grid-label">Delivery address</span>
                <span className="data-grid-value">
                  {readOnly ? (
                    property.charter_delivery_address
                      ? <span style={{ whiteSpace: 'pre-line' }}>{property.charter_delivery_address}</span>
                      : '—'
                  ) : (
                    <textarea
                      className="form-input form-textarea"
                      value={form.charter_delivery_address}
                      onChange={(e) => setForm((f) => ({ ...f, charter_delivery_address: e.target.value }))}
                      rows={3}
                      placeholder="Where the framed Charter is sent"
                      style={{ fontSize: 'var(--text-base)' }}
                    />
                  )}
                </span>
              </div>
              <div className="data-grid-item">
                <span className="data-grid-label">Delivered</span>
                <span className="data-grid-value">
                  {readOnly ? (
                    property.charter_delivered_at
                      ? `Posted on ${formatDate(property.charter_delivered_at)}`
                      : 'Not yet delivered'
                  ) : (
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={form.charter_delivered}
                        onChange={(e) => setForm((f) => ({ ...f, charter_delivered: e.target.checked }))}
                      />
                      <span style={{ fontSize: 'var(--text-base)' }}>
                        {form.charter_delivered
                          ? (property.charter_delivered_at
                              ? `Delivered on ${formatDate(property.charter_delivered_at)}`
                              : 'Delivered (save to persist)')
                          : 'Not yet delivered'}
                      </span>
                    </label>
                  )}
                </span>
              </div>
              <div className="data-grid-item" style={{ borderBottom: 'none' }}>
                <span className="data-grid-label">Courier note</span>
                <span className="data-grid-value">
                  {readOnly ? (
                    property.charter_delivery_notes || '—'
                  ) : (
                    <input
                      type="text"
                      className="form-input"
                      value={form.charter_delivery_notes}
                      onChange={(e) => setForm((f) => ({ ...f, charter_delivery_notes: e.target.value }))}
                      placeholder="Courier reference, contact, etc."
                      style={{ fontSize: 'var(--text-base)' }}
                    />
                  )}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '1rem' }}>
              <button
                type="button"
                className="btn btn-primary btn-pos-gold"
                onClick={openCharterSuite}
                title="Open this property's Charter Suite in a new tab, signed in as admin"
              >
                <span style={{ marginRight: '0.375rem' }}>🔗</span>
                Open Charter Suite
              </button>
              <button
                type="button"
                className="btn"
                style={{ borderColor: POS_GOLD, color: 'var(--color-sidebar)' }}
                onClick={() => navigate(`/charters?tab=suite&invite_property=${property.id}`)}
                title="Invite a Property Manager to this property's Charter Suite"
              >
                <span style={{ marginRight: '0.375rem' }}>✉️</span>
                Send Suite Invite
              </button>
            </div>
          </>
        ) : (
          <p className="text-muted fs-sm" style={{ margin: 0 }}>
            Charter will be issued automatically when the property is active.
          </p>
        )}
      </SectionCard>

      {/* 3. BEDSIDE CERTIFICATE — Charter + Charter Suite cluster. */}
      {property.charter_number && (
        <SectionCard title="Bedside certificate">
          <p className="text-muted fs-sm" style={{ marginTop: 0, marginBottom: '0.875rem' }}>
            The framed POS Certificate that sits in each guest room. Print the cert and stick the QR on the bedside copy.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {posCertUrl && (
              <a
                href={posCertUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
                style={{ textDecoration: 'none', color: POS_GOLD, borderColor: POS_GOLD }}
              >
                Open POS Certificate
              </a>
            )}
            {verifyUrl && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  void downloadQrAsPng(verifyUrl, property.charter_number!);
                }}
                title="Download the QR sticker (1024px PNG, white background)"
              >
                ⬇ Download QR
              </button>
            )}
          </div>
          <p className="text-muted fs-xs" style={{ margin: '0.625rem 0 0' }}>
            Need SVG or transparent formats for a designer? Open the property&apos;s Charter Suite (from the Charter section above) for the full asset set.
          </p>
        </SectionCard>
      )}

      {/* 4. PEOPLE */}
      <SectionCard title="People">
        {/* Customer — full width row with name + contact details stacked, change button right-aligned. */}
        <div style={{ marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--color-border, #e5e7eb)' }}>
          <Field label="Customer">
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ fontWeight: 500 }}>{customerName}</div>
                {property.customer?.email && (
                  <div style={{ marginTop: '0.125rem' }}>
                    <EmailWithStatus
                      email={property.customer.email}
                      isBouncing={property.customer.email_bouncing ?? undefined}
                      lastBounceAt={property.customer.email_last_bounce_at}
                      className="fw-400"
                    />
                  </div>
                )}
                {property.customer?.phone && (
                  <div style={{ color: 'var(--color-text-light)', marginTop: '0.125rem' }}>{property.customer.phone}</div>
                )}
              </div>
              {!readOnly && (
                <button
                  type="button"
                  className="btn btn-sm btn-secondary"
                  onClick={() => setShowCustomerPicker(true)}
                >
                  Change customer
                </button>
              )}
            </div>
            {customerChangeStaged && (
              <p style={{ fontSize: 'var(--text-md)', color: 'var(--color-warning)', marginTop: '0.5rem', marginBottom: 0 }}>
                Customer change staged. Click <strong>Save Changes</strong> in the footer to persist.
              </p>
            )}
          </Field>
        </div>
        {/* Ambassador + Manager — 2 col */}
        <div style={TWO_COL_GRID}>
          <Field label="Ambassador">
            {(readOnly || !canSetAmbassador) ? (
              <>
                {ambassadorDisplay}
                {!readOnly && <span className="form-help">Assigned by an admin.</span>}
              </>
            ) : (
              <SearchSelect
                options={[{ value: '', label: 'None' }, ...ambassadors.map((a) => ({ value: a.id, label: a.name }))]}
                value={form.ambassador_id}
                onChange={(v) => setForm((f) => ({ ...f, ambassador_id: v }))}
              />
            )}
          </Field>
          <Field label="Manager">
            {readOnly ? managerDisplay : (
              <SearchSelect
                options={[{ value: '', label: 'None' }, ...assignableUsers.map((u) => ({ value: u.id, label: u.name }))]}
                value={form.assigned_manager_id}
                onChange={(v) => setForm((f) => ({ ...f, assigned_manager_id: v }))}
              />
            )}
          </Field>
        </div>
      </SectionCard>

      {/* 5. PUBLIC PAGES */}
      {(verifyUrl || listingUrl) && (
        <SectionCard title="Public pages">
          <div className="data-grid">
            {verifyUrl && (
              <div className="data-grid-item" style={{ alignItems: 'flex-start' }}>
                <span className="data-grid-label">Verify page</span>
                <span className="data-grid-value" style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <code style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', wordBreak: 'break-all' }}>
                      {verifyUrl}
                    </code>
                    <a href={verifyUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary">
                      Open
                    </a>
                    <CopyButton value={verifyUrl} />
                  </div>
                  <p className="text-muted fs-xs" style={{ margin: '0.25rem 0 0' }}>
                    What a guest sees when they scan the QR on the bedside certificate.
                  </p>
                </span>
              </div>
            )}
            <div className="data-grid-item" style={{ borderBottom: 'none', alignItems: 'flex-start' }}>
              <span className="data-grid-label">Directory listing</span>
              <span className="data-grid-value" style={{ flex: 1 }}>
                {listingUrl ? (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <code style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', wordBreak: 'break-all' }}>
                        {listingUrl}
                      </code>
                      <a href={listingUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-secondary">
                        Open
                      </a>
                      <CopyButton value={listingUrl} />
                    </div>
                    <p className="text-muted fs-xs" style={{ margin: '0.25rem 0 0' }}>
                      The property&apos;s premium listing on the public directory.
                    </p>
                  </>
                ) : (
                  <p className="text-muted fs-sm" style={{ margin: 0 }}>
                    Not on the public directory yet. Publish the property from the Property tab&apos;s Listing section.
                  </p>
                )}
              </span>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Customer picker sub-modal */}
      {showCustomerPicker && (
        <div className="modal-overlay" onClick={() => setShowCustomerPicker(false)} style={{ zIndex: 1100 }}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Select customer</h2>
              <button className="modal-close" onClick={() => setShowCustomerPicker(false)}>&times;</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <CustomerForm
                supabase={supabase}
                onSave={onCustomerPicked}
                onCancel={() => setShowCustomerPicker(false)}
                globalSearchMode
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
